import { useEffect, useMemo, useRef, useState } from "react"
import { FEATURED_PROJECTS, GRAPHQL_URL, PROJECTS_PER_PAGE, FeaturedProject } from "config"
import { getMarketplaceWorkDetailHref, MARKETPLACE_WORKS, MarketplaceWork } from "data/marketplaceWorks"
import useProjects from "hooks/useProjects"
import { fetchArtBlocksProjectTokens } from "utils/artBlocksMetadataApi"
import { getContractChainId } from "utils/contractInfoHelper"
import { fetchErc721Owner } from "utils/erc721Owner"
import { OrderDirection, Project } from "utils/types"

type HeroArtworkKind = "series" | "one-of-one"

interface SeriesRelease {
  kind: "series"
  id: string
  name: string
  contractAddress: string
  projectId: string
  chainId: number
  invocationCount: number
  aspectRatio?: number
  createdAt?: string | number | BigInt
  detailHref: string
  featuredProject?: FeaturedProject
}

interface OneOfOneRelease {
  kind: "one-of-one"
  id: string
  work: MarketplaceWork
}

export interface RandomHeroArtwork {
  kind: HeroArtworkKind
  title: string
  label: string
  year?: string
  ownerAddress?: string
  href: string
  aspectRatio: number
  tokenId?: string
  invocation?: number
  chainId?: number
  contractAddress?: string
  featuredProject?: FeaturedProject
  mediaUrl?: string
  mediaType?: "image" | "video"
}

const getRandomIndex = (length: number) => Math.floor(Math.random() * length)

const getProjectNumber = (value: Project["projectId"]) => value.toString()

const getProjectInvocationCount = (project: Project) => Number(project.invocations || 0)

const getYearFromTimestamp = (timestamp?: string | number | BigInt) => {
  if (timestamp === undefined || timestamp === null) return undefined

  if (typeof timestamp === "string" && Number.isNaN(Number(timestamp))) {
    const date = new Date(timestamp)
    return Number.isNaN(date.getTime()) ? undefined : date.getFullYear().toString()
  }

  const seconds = Number(timestamp)
  if (!Number.isFinite(seconds) || seconds <= 0) return undefined

  return new Date(seconds * 1000).getFullYear().toString()
}

const getYearFromMintedAt = (mintedAt?: string) => {
  if (!mintedAt) return undefined
  const date = new Date(mintedAt)
  return Number.isNaN(date.getTime()) ? undefined : date.getFullYear().toString()
}

const projectToRelease = (project: Project): SeriesRelease | null => {
  const chainId = getContractChainId(project.contract.id)
  const invocationCount = getProjectInvocationCount(project)
  if (!chainId || invocationCount <= 0) return null

  const projectId = getProjectNumber(project.projectId)

  return {
    kind: "series",
    id: project.id,
    name: project.name,
    contractAddress: project.contract.id,
    projectId,
    chainId,
    invocationCount,
    aspectRatio: project.aspectRatio,
    createdAt: project.createdAt,
    detailHref: `/project/${project.contract.id}/${projectId}`,
  }
}

const featuredProjectToRelease = (project: FeaturedProject): SeriesRelease => ({
  kind: "series",
  id: project.key,
  name: project.name,
  contractAddress: project.contractAddress,
  projectId: project.projectId,
  chainId: project.chainId,
  invocationCount: 0,
  aspectRatio: project.aspectRatio,
  detailHref: project.detailPath,
  featuredProject: project,
})

const resolveSeriesArtwork = async (release: SeriesRelease): Promise<RandomHeroArtwork | null> => {
  const invocationCount = release.invocationCount > 0
    ? release.invocationCount
    : (await fetchArtBlocksProjectTokens({
        contractAddress: release.contractAddress,
        projectId: release.projectId,
        chainId: release.chainId,
        limit: 1,
        offset: 0,
        orderDirection: OrderDirection.ASC,
      }))?.tokens_aggregate?.aggregate?.count || 1
  const randomOffset = getRandomIndex(Math.max(1, invocationCount))
  const projectMetadata = await fetchArtBlocksProjectTokens({
    contractAddress: release.contractAddress,
    projectId: release.projectId,
    chainId: release.chainId,
    limit: 1,
    offset: randomOffset,
    orderDirection: OrderDirection.ASC,
  })
  const token = projectMetadata?.tokens?.[0]

  if (!token) return null

  const invocation = Number(token.invocation)
  const year = getYearFromMintedAt(token.minted_at) || getYearFromTimestamp(release.createdAt)
  const href = release.featuredProject
    ? `${release.featuredProject.detailPath}/${token.token_id}`
    : `/token/${release.contractAddress}/${token.token_id}`

  return {
    kind: "series",
    title: release.name,
    label: `${release.name} #${Number.isFinite(invocation) ? invocation : token.token_id}`,
    year,
    ownerAddress: token.owner_address,
    href,
    aspectRatio: projectMetadata?.aspect_ratio || release.aspectRatio || 1,
    tokenId: token.token_id,
    invocation,
    chainId: release.chainId,
    contractAddress: release.contractAddress,
    featuredProject: release.featuredProject,
  }
}

const resolveOneOfOneArtwork = async (release: OneOfOneRelease): Promise<RandomHeroArtwork> => {
  let ownerAddress: string | undefined

  try {
    ownerAddress = await fetchErc721Owner(release.work.contractAddress, release.work.tokenId)
  } catch {
    ownerAddress = undefined
  }

  return {
    kind: "one-of-one",
    title: release.work.title,
    label: `${release.work.title} 1/1`,
    year: release.work.year,
    ownerAddress,
    href: getMarketplaceWorkDetailHref(release.work),
    aspectRatio: release.work.aspectRatio,
    mediaUrl: release.work.mediaUrl,
    mediaType: release.work.mediaType,
  }
}

const useRandomHeroArtwork = () => {
  const hasGraphqlEndpoint = Boolean(GRAPHQL_URL)
  const selectedReleaseId = useRef<string | null>(null)
  const [artwork, setArtwork] = useState<RandomHeroArtwork | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const { loading: projectsLoading, data: projectsData } = useProjects({
    first: PROJECTS_PER_PAGE,
    orderDirection: OrderDirection.DESC,
  })

  const releases = useMemo(() => {
    const featuredSeries = FEATURED_PROJECTS.map(featuredProjectToRelease)
    const artBlocksSeries = hasGraphqlEndpoint
      ? ((projectsData?.projects || []) as Project[])
          .map(projectToRelease)
          .filter((release: SeriesRelease | null): release is SeriesRelease => Boolean(release))
      : []
    const oneOfOnes: OneOfOneRelease[] = MARKETPLACE_WORKS.map((work) => ({
      kind: "one-of-one",
      id: work.id,
      work,
    }))

    return [...featuredSeries, ...artBlocksSeries, ...oneOfOnes]
  }, [hasGraphqlEndpoint, projectsData?.projects])

  useEffect(() => {
    if (hasGraphqlEndpoint && projectsLoading && !projectsData?.projects?.length) return
    if (releases.length === 0) return

    const release = selectedReleaseId.current
      ? releases.find((candidate) => candidate.id === selectedReleaseId.current)
      : releases[getRandomIndex(releases.length)]

    if (!release) return

    selectedReleaseId.current = release.id
    let cancelled = false

    setLoading(true)
    setError(false)

    const resolveArtwork = release.kind === "series"
      ? resolveSeriesArtwork(release)
      : resolveOneOfOneArtwork(release)

    resolveArtwork
      .then((nextArtwork) => {
        if (!cancelled) setArtwork(nextArtwork)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [hasGraphqlEndpoint, projectsData?.projects?.length, projectsLoading, releases])

  return {
    artwork,
    loading,
    error,
  }
}

export default useRandomHeroArtwork
